/* eslint-disable no-unused-vars */
import Vue from "vue";
import { jwtDecode } from "jwt-decode";
import Cookies from "js-cookie";

import {
  PublicClientApplication,
  LogLevel,
  InteractionRequiredAuthError,
} from "@azure/msal-browser";

// MSAL/Azure AD B2C configuration
const authorityDomain = process.env.VUE_APP_AUTHORITY_DOMAIN;
const signInPolicy = process.env.VUE_APP_SIGNIN_POLICY;
const passwordResetPolicy = process.env.VUE_APP_PASSWORD_RESET_POLICY;
const clientId = process.env.VUE_APP_CLIENT_ID;
const tenant = process.env.VUE_APP_TENANT;
const tokenAPIScopes = [
  process.env.VUE_APP_TOKEN_API_SCOPES,
];
const loginScopes = ["openid", ...tokenAPIScopes];

const msalConfig = {
  auth: {
    clientId,
    authority: `https://${authorityDomain}/${tenant}/${signInPolicy}`,
    knownAuthorities: [authorityDomain],
    navigateToLoginRequestUrl: false,
    postLogoutRedirectUri: "https://d3bokly6r3uvec.cloudfront.net/login",
    redirectUri: "https://d3bokly6r3uvec.cloudfront.net/login",
  },
  cache: {
    cacheLocation: "sessionStorage",
    storeAuthStateInCookie: false,
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) return;
        switch (level) {
          case LogLevel.Error:
            console.error(message);
            break;
          case LogLevel.Info:
            console.info(message);
            break;
          case LogLevel.Verbose:
            console.debug(message);
            break;
          case LogLevel.Warning:
            console.warn(message);
            break;
        }
      },
    },
  },
};

const msalInstance = new PublicClientApplication(msalConfig);
let msalReady = false;
const initializeMSAL = async () => {
  if (msalReady) return;
  try {
    await msalInstance.initialize();
    msalReady = true;
    console.log("MSAL initialized");
  } catch (error) {
    console.error("MSAL initialization error:", error);
  }
};

// Helper to log token expiry time in minutes and seconds
function logTokenExpiry(refreshIn) {
  const minutes = Math.floor(refreshIn / 60000);
  const seconds = Math.floor((refreshIn % 60000) / 1000);
  console.log(
    `\x1b[32mToken will be renewed in ${minutes}m ${seconds}s\x1b[0m`
  );
}

export const state = () => ({
  msalAccount: null,
  accessToken: "",
  refreshTimeoutId: null,
  isPasswordResetFlow: false,
});

export const getters = {
  // Returns user info from msalAccount and decoded accessToken
  userInfo: (state) => {
    // return name, given_name, family_name, email from the access token
    if (!state.accessToken) return null;
    const decoded = jwtDecode(state.accessToken);
    return {
      name: decoded.name,
      given_name: decoded.given_name,
      family_name: decoded.family_name,
      email: decoded.email,
      sub: decoded.sub,
    };
  },
  // Returns true if the user is authenticated
  isAuthenticated: (state) => !!state.accessToken,
};

export const mutations = {
  setAccount(state, account) {
    state.msalAccount = account;
  },
  setAccessToken(state, token) {
    //console.log("\x1b[96mSetting access token to\x1b[0m \x1b[96m%s\x1b[0m", token);
    state.accessToken = token;
  },
  clearAccount(state) {
    state.msalAccount = null;
  },
  clearAccessToken(state) {
    state.accessToken = "";
    if (state.refreshTimeoutId) {
      clearTimeout(state.refreshTimeoutId);
      state.refreshTimeoutId = null;
    }
  },
  setRefreshTimeout(state, timeoutId) {
    state.refreshTimeoutId = timeoutId;
  },
  clearRefreshTimeout(state) {
    if (state.refreshTimeoutId) {
      clearTimeout(state.refreshTimeoutId);
      state.refreshTimeoutId = null;
    }
  },
};

export const actions = {
  // Start sign-in flow
  async signIn() {
    await initializeMSAL();
    const loginRequest = {
      scopes: loginScopes,
      authority: msalConfig.auth.authority,
    };
    try {
      await msalInstance.loginRedirect(loginRequest);
    } catch (error) {
      console.error("Login redirect error:", error);
    }
  },

  // Start forgot password flow
  async forgotPassword() {
    await initializeMSAL();
    const passwordResetRequest = {
      scopes: loginScopes,
      authority: `https://${authorityDomain}/${tenant}/${passwordResetPolicy}`,
    };
    try {
      await msalInstance.loginRedirect(passwordResetRequest);
    } catch (error) {
      console.error("Password reset redirect error:", error);
    }
  },

  // Handle redirect after login
  async handleRedirect({ dispatch }) {
    await initializeMSAL();
    try {
      const response = await msalInstance.handleRedirectPromise();
      if (response) {
        if (
          response.idTokenClaims?.acr?.toUpperCase() === passwordResetPolicy
        ) {
          // TODO: display a message to the user that their password has been reset
          alert("Password reset successful.");
        }

        await dispatch("getAccessToken");
      }

      console.log("handleRedirect complete");
    } catch (error) {
      if (error?.errorMessage?.includes("AADB2C90118")) {
        await dispatch("forgotPassword");
        return;
      }

      if (error instanceof InteractionRequiredAuthError) {
        console.warning("Interaction required for authentication:", error);
      } else {
        console.warning("Error handling redirect:", error);
      }
    }
  },

  // Acquire access token silently, fallback to redirect if needed
  async getAccessToken({ state, commit, dispatch }) {
    await initializeMSAL();
    await dispatch("selectAccount");
    const account = state.msalAccount
      ? msalInstance.getAccount({
          homeAccountId: state.msalAccount.homeAccountId,
        })
      : null;
    if (!account) {
      console.warn("No account found for token acquisition");
      return;
    }
    const tokenRequest = {
      account,
      scopes: tokenAPIScopes,
      authority: msalConfig.auth.authority,
    };
    let accessToken = "";
    try {
      const response = await msalInstance.acquireTokenSilent(tokenRequest);
      accessToken = response.accessToken;
      console.log("Access token acquired silently");
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        try {
          await msalInstance.acquireTokenRedirect(tokenRequest);
          // Token will be handled after redirect
          return;
        } catch (interactiveError) {
          console.error(
            "Interactive token acquisition error:",
            interactiveError
          );
        }
      } else {
        console.error("Error acquiring access token:", error);
      }
    }
    if (accessToken) {
      try {
        const decodedToken = jwtDecode(accessToken);
        const expiryDate = new Date(decodedToken.exp * 1000);
        Cookies.set("authToken", accessToken, {
          expires: expiryDate,
          path: "/",
          secure: true,
          sameSite: "Strict",
        });
        commit("setAccessToken", accessToken);
        await dispatch("scheduleTokenRefresh");
        console.log("Access token set and scheduled refresh");
      } catch (e) {
        console.warn("Failed to decode/set cookie for access token");
      }
    }
  },

  // Select the correct account from MSAL
  async selectAccount({ commit, dispatch }) {
    await initializeMSAL();
    const allAccounts = msalInstance.getAllAccounts();
    if (!allAccounts.length) {
      console.warn("No accounts found in MSAL");
      return;
    }
    // Filter for accounts matching our policy and client
    const matchingAccounts = allAccounts.filter(
      (account) =>
        account.homeAccountId &&
        account.homeAccountId
          .toUpperCase()
          .includes(signInPolicy.toUpperCase()) &&
        account.idTokenClaims &&
        account.idTokenClaims.iss &&
        account.idTokenClaims.iss
          .toUpperCase()
          .includes(authorityDomain.toUpperCase()) &&
        account.idTokenClaims.aud === clientId
    );
    let selected = null;
    if (matchingAccounts.length === 1) {
      selected = matchingAccounts[0];
    } else if (matchingAccounts.length > 1) {
      // If all accounts are for the same user, pick the first
      if (
        matchingAccounts.every(
          (a) => a.localAccountId === matchingAccounts[0].localAccountId
        )
      ) {
        selected = matchingAccounts[0];
      } else {
        // Multiple users detected, sign out all
        console.warn("Multiple users detected, logging out all.");
        await dispatch("signOut");
        return;
      }
    } else if (allAccounts.length === 1) {
      selected = allAccounts[0];
    }
    if (selected) {
      commit("setAccount", selected);
      console.log("Account set:", selected);
    } else {
      console.warn("No matching accounts found.");
    }
  },

  // Sign out and redirect
  async signOut() {
    await initializeMSAL();
    Cookies.remove("authToken");
    try {
      await msalInstance.logoutRedirect({
        postLogoutRedirectUri: msalConfig.auth.postLogoutRedirectUri,
      });
      console.log("User signed out successfully");
    } catch (error) {
      console.error("Error signing out:", error);
    }
  },

  // Schedules a token refresh timeout
  scheduleTokenRefresh({ state, commit, dispatch }) {
    if (state.refreshTimeoutId) {
      commit("clearRefreshTimeout");
    }
    if (!state.accessToken) return;
    let decoded;
    try {
      decoded = jwtDecode(state.accessToken);
    } catch (e) {
      return;
    }
    if (!decoded.exp) return;
    const now = Math.floor(Date.now() / 1000);
    const refreshIn = (decoded.exp - 120 - now) * 1000; // ms until 120s before expiry
    if (refreshIn <= 0) {
      // Already expired or within 2 minutes, refresh immediately
      dispatch("getAccessToken");
      return;
    }
    logTokenExpiry(refreshIn);
    commit(
      "setRefreshTimeout",
      setTimeout(() => {
        dispatch("getAccessToken");
      }, refreshIn)
    );
  },

  async loadTokenFromStorage({ commit, dispatch }) {
    const token = Cookies.get("authToken");
    if (token) {
      console.log("Token found, setting access token");
      commit("setAccessToken", token);
      await dispatch("scheduleTokenRefresh");
    } else {
      console.log("No token found, clearing access token");
      commit("clearAccessToken");
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
